import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CalendarCheck2, ChevronDown } from 'lucide-react'
import { publicAgendaService } from '../../services/api'
import { asArray, buildMonthGrid, formatTimeLabel, getDateKey } from './scheduleUtils'

const monthTitle = (date) => new Intl.DateTimeFormat('es-CO', { month: 'long', year: 'numeric' }).format(date)
const money = (value) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(value || 0))
const unwrapPayload = (response) => response?.data?.data ?? response?.data ?? null

const normalizeService = (service) => ({
  id: service?.id || service?.Id || '',
  name: service?.name || service?.Name || 'Servicio',
  description: service?.description || service?.Description || '',
  price: Number(service?.price ?? service?.Price ?? 0),
  durationMinutes: Number(service?.durationMinutes ?? service?.DurationMinutes ?? 0)
})

const normalizeSlot = (slot) => ({
  startAtUtc: slot?.startAtUtc || slot?.StartAtUtc || '',
  endAtUtc: slot?.endAtUtc || slot?.EndAtUtc || '',
  serviceId: slot?.serviceId || slot?.ServiceId || null
})

const normalizeReservation = (item) => ({
  appointmentId: item?.appointmentId || item?.AppointmentId || '',
  serviceId: item?.serviceId || item?.ServiceId || '',
  serviceName: item?.serviceName || item?.ServiceName || 'Servicio',
  startAtUtc: item?.startAtUtc || item?.StartAtUtc || '',
  endAtUtc: item?.endAtUtc || item?.EndAtUtc || '',
  price: Number(item?.price ?? item?.Price ?? 0)
})

function BookingSection({ title, description = '', mobile, isOpen, onToggle, children, actions = null }) {
  return (
    <section className="rounded-[28px] border border-stone-200 bg-white p-6 shadow-[0_24px_60px_rgba(15,23,42,0.08)]">
      {mobile ? (
        <>
          <button
            type="button"
            onClick={onToggle}
            className="flex w-full items-center justify-between gap-4 text-left"
            aria-expanded={isOpen}
          >
            <div>
              <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
              {description && <p className="mt-1 pr-3 text-sm leading-6 text-slate-500">{description}</p>}
            </div>
            <span className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-stone-200 bg-stone-50 text-slate-600 transition ${isOpen ? 'rotate-180' : ''}`}>
              <ChevronDown className="h-4 w-4" />
            </span>
          </button>
          {isOpen && <div className="mt-4">{children}</div>}
        </>
      ) : (
        <>
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
              {description && <p className="mt-1 text-sm leading-6 text-slate-500">{description}</p>}
            </div>
            {actions}
          </div>
          <div className="mt-4">{children}</div>
        </>
      )}
    </section>
  )
}

export default function PublicAgendaBooking() {
  const { ownerUserId } = useParams()
  const [agenda, setAgenda] = useState(null)
  const [monthDate, setMonthDate] = useState(() => new Date())
  const [selectedServiceId, setSelectedServiceId] = useState(null)
  const [availability, setAvailability] = useState([])
  const [selectedDateKey, setSelectedDateKey] = useState('')
  const [selectedSlots, setSelectedSlots] = useState([])
  const [isSlotsModalOpen, setIsSlotsModalOpen] = useState(false)
  const [isReservationsModalOpen, setIsReservationsModalOpen] = useState(false)
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [invitationCode, setInvitationCode] = useState('')
  const [validation, setValidation] = useState(null)
  const [clientName, setClientName] = useState('')
  const [clientPhone, setClientPhone] = useState('')
  const [clientEmail, setClientEmail] = useState('')
  const [notes, setNotes] = useState('')
  const [confirmation, setConfirmation] = useState(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [availabilityVersion, setAvailabilityVersion] = useState(0)
  const [isMobileView, setIsMobileView] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false))
  const [mobileSections, setMobileSections] = useState({
    invitation: true,
    service: false,
    client: false,
    availability: false,
    summary: false
  })

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        setError('')
        const response = await publicAgendaService.getAgenda(ownerUserId)
        const payload = unwrapPayload(response)
        setAgenda(payload)
        const firstServiceId = asArray(payload?.services || payload?.Services).map(normalizeService)[0]?.id || null
        setSelectedServiceId(firstServiceId)
      } catch (err) {
        setError(err?.message || 'No fue posible cargar la agenda publica.')
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [ownerUserId])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    const syncViewport = () => {
      const mobile = window.innerWidth < 768
      setIsMobileView(mobile)
      if (!mobile) {
        setMobileSections({
          invitation: true,
          service: true,
          client: true,
          availability: true,
          summary: true
        })
      }
    }

    syncViewport()
    window.addEventListener('resize', syncViewport)
    return () => window.removeEventListener('resize', syncViewport)
  }, [])

  useEffect(() => {
    const loadAvailability = async () => {
      if (!selectedServiceId) return
      try {
        setError('')
        const from = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1)
        const to = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1)
        const response = await publicAgendaService.getAvailability(ownerUserId, selectedServiceId, from.toISOString(), to.toISOString())
        setAvailability(asArray(unwrapPayload(response)).map(normalizeSlot))
      } catch (err) {
        setError(err?.message || 'No se pudo cargar la disponibilidad.')
        setAvailability([])
      }
    }

    loadAvailability()
  }, [availabilityVersion, monthDate, ownerUserId, selectedServiceId])

  const services = useMemo(
    () => asArray(agenda?.services || agenda?.Services).map(normalizeService),
    [agenda]
  )

  const selectedService = useMemo(
    () => services.find((service) => service.id === selectedServiceId) || null,
    [services, selectedServiceId]
  )

  const slotsByDay = useMemo(() => {
    return availability.reduce((acc, slot) => {
      const key = getDateKey(new Date(slot.startAtUtc))
      if (!acc[key]) acc[key] = []
      acc[key].push(slot)
      return acc
    }, {})
  }, [availability])

  const monthGrid = useMemo(() => buildMonthGrid(monthDate), [monthDate])
  const todayKey = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return getDateKey(today)
  }, [])

  const daySlots = useMemo(() => slotsByDay[selectedDateKey] || [], [selectedDateKey, slotsByDay])

  const totalAmount = useMemo(() => Number(selectedService?.price || 0) * selectedSlots.length, [selectedService, selectedSlots.length])
  const selectedDateLabel = useMemo(() => {
    if (!selectedDateKey) return ''
    return new Date(`${selectedDateKey}T00:00:00`).toLocaleDateString('es-CO', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    })
  }, [selectedDateKey])
  const daySlotGroups = useMemo(() => {
    return daySlots.reduce((acc, slot) => {
      const hour = new Date(slot.startAtUtc).getHours()
      const groupKey = hour < 12 ? 'Manana' : hour < 18 ? 'Tarde' : 'Noche'
      if (!acc[groupKey]) acc[groupKey] = []
      acc[groupKey].push(slot)
      return acc
    }, {})
  }, [daySlots])
  const orderedDaySlotGroups = useMemo(() => (
    ['Manana', 'Tarde', 'Noche']
      .filter((groupName) => Array.isArray(daySlotGroups[groupName]) && daySlotGroups[groupName].length > 0)
      .map((groupName) => [
        groupName,
        [...daySlotGroups[groupName]].sort((left, right) => (
          new Date(left.startAtUtc).getTime() - new Date(right.startAtUtc).getTime()
        ))
      ])
  ), [daySlotGroups])
  const existingReservations = useMemo(
    () => asArray(validation?.existingReservations || validation?.ExistingReservations).map(normalizeReservation),
    [validation]
  )
  const allKnownReservations = useMemo(() => {
    const latestReservations = asArray(confirmation?.reservations || confirmation?.Reservations).map(normalizeReservation)
    const byId = new Map()

    existingReservations.forEach((item) => byId.set(item.appointmentId, item))
    latestReservations.forEach((item) => byId.set(item.appointmentId, item))

    return [...byId.values()].sort((left, right) => (
      new Date(left.startAtUtc).getTime() - new Date(right.startAtUtc).getTime()
    ))
  }, [confirmation, existingReservations])
  const reservationTotals = useMemo(() => ({
    count: allKnownReservations.length,
    amount: allKnownReservations.reduce((sum, item) => sum + Number(item.price || 0), 0)
  }), [allKnownReservations])
  const latestConfirmationReservations = useMemo(
    () => asArray(confirmation?.reservations || confirmation?.Reservations).map(normalizeReservation),
    [confirmation]
  )

  useEffect(() => {
    const availableDays = Object.keys(slotsByDay).sort()
    if (availableDays.length === 0) {
      setSelectedDateKey('')
      return
    }

    if (!selectedDateKey || !slotsByDay[selectedDateKey]) {
      setSelectedDateKey(availableDays[0])
    }
  }, [selectedDateKey, slotsByDay])

  const validateInvitation = async () => {
    try {
      setError('')
      const response = await publicAgendaService.validateInvitation(ownerUserId, invitationCode)
      const payload = unwrapPayload(response)
      setValidation(payload)
      if (!payload?.isValid) {
        setSelectedSlots([])
      }
      if (payload?.clientName) {
        setClientName(payload.clientName)
      }
    } catch (err) {
      setError(err?.message || 'No fue posible validar el codigo.')
      setValidation(null)
    }
  }

  const toggleSlot = (slot) => {
    const key = `${slot.serviceId || selectedServiceId}-${slot.startAtUtc}`
    setSelectedSlots((current) => {
      const exists = current.some((item) => `${item.serviceId}-${item.startAtUtc}` === key)
      if (exists) {
        return current.filter((item) => `${item.serviceId}-${item.startAtUtc}` !== key)
      }

      return [...current, { serviceId: selectedServiceId, startAtUtc: slot.startAtUtc, endAtUtc: slot.endAtUtc }]
    })
  }

  const openDaySlots = (dateKey) => {
    if (dateKey < todayKey) {
      return
    }

    const slots = slotsByDay[dateKey] || []
    if (slots.length === 0) {
      return
    }

    setSelectedDateKey(dateKey)
    setIsSlotsModalOpen(true)
  }

  const toggleMobileSection = (sectionKey) => {
    setMobileSections((current) => ({
      ...current,
      [sectionKey]: !current[sectionKey]
    }))
  }

  const reserve = async () => {
    try {
      setError('')
      const response = await publicAgendaService.reserve(ownerUserId, {
        invitationCode,
        clientName,
        clientPhone,
        clientEmail,
        notes,
        items: selectedSlots.map((slot) => ({ serviceId: slot.serviceId, startAtUtc: slot.startAtUtc }))
      })
      const payload = unwrapPayload(response)
      setConfirmation(payload)
      setValidation((current) => {
        if (!current) return current
        const currentReservations = asArray(current.existingReservations || current.ExistingReservations).map(normalizeReservation)
        const newReservations = asArray(payload?.reservations || payload?.Reservations).map(normalizeReservation)
        const byId = new Map()
        currentReservations.forEach((item) => byId.set(item.appointmentId, item))
        newReservations.forEach((item) => byId.set(item.appointmentId, item))

        return {
          ...current,
          existingReservations: [...byId.values()]
        }
      })
      setSelectedSlots([])
      setAvailabilityVersion((current) => current + 1)
      setIsSlotsModalOpen(false)
      setIsConfirmationModalOpen(true)
    } catch (err) {
      const message = err?.message === 'Ese horario ya no está disponible.'
        ? 'Ese espacio ya no está disponible. Elige otro horario.'
        : (err?.message || 'No fue posible crear la reserva.')
      setError(message)
      setAvailabilityVersion((current) => current + 1)
    }
  }

  if (loading) {
    return <div className="min-h-screen bg-stone-50 flex items-center justify-center text-gray-600">Cargando agenda...</div>
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#f8faf8_0%,#fff8f6_100%)] px-4 py-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <section className="rounded-[28px] border border-stone-200 bg-white/95 p-6 shadow-[0_24px_60px_rgba(15,23,42,0.08)]">
          <p className="text-xs uppercase tracking-[0.22em] text-stone-500">Agenda publica</p>
          <h1 className="mt-3 text-3xl font-display text-slate-900">{agenda?.displayName || agenda?.DisplayName || 'AgendaLab'}</h1>
          <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600">{agenda?.description || agenda?.Description || 'Selecciona un servicio, revisa la disponibilidad y reserva tus espacios.'}</p>
        </section>

        {error && <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

        <div className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
            <section className="space-y-6">
              <BookingSection
                title="1. Valida tu codigo de invitacion"
                description="Ingresa tu codigo para identificar tus reservas y habilitar el agendamiento."
                mobile={isMobileView}
                isOpen={mobileSections.invitation}
                onToggle={() => toggleMobileSection('invitation')}
              >
                <div className="flex gap-3">
                  <input className="input" value={invitationCode} onChange={(e) => setInvitationCode(e.target.value)} placeholder="Ingresa tu codigo" />
                  <button className="btn btn-primary" type="button" onClick={validateInvitation}>Validar</button>
                </div>
                {validation && (
                  <p className={`mt-3 text-sm ${validation.isValid ? 'text-emerald-700' : 'text-red-700'}`}>
                    {validation.message}
                  </p>
                )}
                {existingReservations.length > 0 && (
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                    <div>
                      <p className="text-sm font-semibold text-emerald-800">Ya tienes reservas con este codigo</p>
                      <p className="mt-1 text-sm text-emerald-700">{reservationTotals.count} reserva(s) · {money(reservationTotals.amount)}</p>
                    </div>
                    <button
                      className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-emerald-300 bg-white text-emerald-700 transition hover:bg-emerald-100"
                      type="button"
                      onClick={() => setIsReservationsModalOpen(true)}
                      title="Ver reservas"
                      aria-label="Ver reservas"
                    >
                      <CalendarCheck2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </BookingSection>

              <BookingSection
                title="2. Tus datos"
                description="Confirma tus datos de contacto para asociarlos a la reserva."
                mobile={isMobileView}
                isOpen={mobileSections.client}
                onToggle={() => toggleMobileSection('client')}
              >
                <div className="grid gap-3 md:grid-cols-2">
                  <input className="input" value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Nombre" />
                  <input className="input" value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} placeholder="Telefono" />
                  <input className="input md:col-span-2" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="Correo electronico" />
                  <textarea className="input md:col-span-2 min-h-[100px]" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notas opcionales" />
                </div>
              </BookingSection>

              <BookingSection
                title="3. Elige un servicio"
                description="Selecciona el servicio del emprendedor que quieres reservar."
                mobile={isMobileView}
                isOpen={mobileSections.service}
                onToggle={() => toggleMobileSection('service')}
              >
                <div className="space-y-4">
                  <select
                    className="input"
                    value={selectedServiceId || ''}
                    onChange={(e) => {
                      setSelectedServiceId(e.target.value)
                      setSelectedSlots([])
                      setSelectedDateKey('')
                    }}
                  >
                    <option value="">Selecciona un servicio</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.name} - {money(service.price)} - {service.durationMinutes} min
                      </option>
                    ))}
                  </select>

                  {services.length > 0 ? (
                    <div className="grid gap-3">
                      {services.map((service) => {
                        const selected = service.id === selectedServiceId

                        return (
                          <button
                            key={service.id}
                            type="button"
                            onClick={() => {
                              setSelectedServiceId(service.id)
                              setSelectedSlots([])
                              setSelectedDateKey('')
                            }}
                            className={`rounded-2xl border p-4 text-left transition ${
                              selected
                                ? 'border-slate-900 bg-slate-50 ring-2 ring-slate-900'
                                : 'border-stone-200 bg-stone-50 hover:border-emerald-300 hover:bg-emerald-50'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <p className="font-semibold text-slate-900">{service.name}</p>
                                <p className="mt-1 text-sm leading-6 text-slate-600">{service.description || 'Sin descripcion adicional.'}</p>
                              </div>
                              <div className="shrink-0 text-right">
                                <div className="font-semibold text-slate-900">{money(service.price)}</div>
                                <div className="text-xs text-slate-500">{service.durationMinutes} min</div>
                              </div>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4 text-sm text-slate-500">
                      Este emprendedor aun no tiene servicios activos para reservar.
                    </div>
                  )}
                </div>
              </BookingSection>
            </section>

            <section className="space-y-6">
              <BookingSection
                title="4. Disponibilidad"
                description="Revisa los dias con cupos y toca uno disponible para ver sus horarios."
                mobile={isMobileView}
                isOpen={mobileSections.availability}
                onToggle={() => toggleMobileSection('availability')}
                actions={(
                  <div className="flex items-center gap-2">
                    <button className="btn btn-secondary" type="button" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))}>Anterior</button>
                    <span className="text-sm font-medium text-slate-700 capitalize">{monthTitle(monthDate)}</span>
                    <button className="btn btn-secondary" type="button" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))}>Siguiente</button>
                  </div>
                )}
              >
                {isMobileView && (
                  <div className="mb-4 flex items-center justify-between gap-2">
                    <button className="btn btn-secondary" type="button" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))}>Anterior</button>
                    <span className="text-sm font-medium text-slate-700 capitalize">{monthTitle(monthDate)}</span>
                    <button className="btn btn-secondary" type="button" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))}>Siguiente</button>
                  </div>
                )}

                <div className="grid grid-cols-7 gap-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-400">
                  {['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'].map((day) => <div key={day}>{day}</div>)}
                </div>

                <div className="mt-2 grid grid-cols-7 gap-2">
                  {monthGrid.map((day) => {
                    const key = getDateKey(day)
                    const slots = slotsByDay[key] || []
                    const isCurrentMonth = day.getMonth() === monthDate.getMonth()
                    const isPastDay = key < todayKey
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => openDaySlots(key)}
                        disabled={isPastDay}
                        className={`min-h-[90px] rounded-2xl border p-3 text-left transition ${isPastDay ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed' : slots.length > 0 ? 'border-emerald-200 bg-emerald-50 hover:border-emerald-300 hover:bg-emerald-100/70 cursor-pointer' : 'border-rose-200 bg-rose-50 cursor-default'} ${selectedDateKey === key && !isPastDay ? 'ring-2 ring-slate-900' : ''} ${isCurrentMonth ? 'opacity-100' : 'opacity-40'}`}
                      >
                        <div className={`text-sm font-semibold ${isPastDay ? 'text-slate-400' : 'text-slate-900'}`}>{day.getDate()}</div>
                        <div className={`mt-4 inline-flex rounded-full px-2 py-1 text-[10px] font-semibold sm:px-2.5 sm:text-xs ${isPastDay ? 'bg-slate-200 text-slate-500' : slots.length > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                          <span className="sm:hidden">{isPastDay ? 'Pasó' : slots.length > 0 ? `${slots.length} D.` : 'N. D.'}</span>
                          <span className="hidden sm:inline">{isPastDay ? 'Pasó' : slots.length > 0 ? `${slots.length} Disp.` : 'No Disp.'}</span>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </BookingSection>

              <BookingSection
                title="5. Resumen de seleccion"
                description="Antes de reservar, revisa cuantos espacios llevas seleccionados y su total."
                mobile={isMobileView}
                isOpen={mobileSections.summary}
                onToggle={() => toggleMobileSection('summary')}
              >
                <p className="text-sm text-slate-600">Haz clic sobre un dia verde para abrir un popup con los horarios exactos del servicio y reservar desde alli.</p>
                <div className="mt-4 text-sm text-slate-700">Espacios seleccionados: <strong>{selectedSlots.length}</strong></div>
                <div className="mt-2 text-sm text-slate-700">Total a pagar: <strong>{money(totalAmount)}</strong></div>
                {!validation?.isValid && (
                  <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                    Valida tu codigo de invitacion antes de reservar.
                  </div>
                )}
              </BookingSection>
            </section>
        </div>
      </div>

      {isSlotsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 px-4 py-6">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-[28px] bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Horarios disponibles</p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900 capitalize">{selectedDateLabel}</h2>
                <p className="mt-2 text-sm text-slate-600">
                  {selectedService?.name || 'Servicio'} de {selectedService?.durationMinutes || 0} minutos. Cada tarjeta es un espacio real que puedes reservar dentro de la programacion del emprendedor.
                </p>
              </div>
              <button className="btn btn-secondary" type="button" onClick={() => setIsSlotsModalOpen(false)}>
                Cerrar
              </button>
            </div>

            <div className="mt-6 space-y-5">
              {orderedDaySlotGroups.length > 0 ? orderedDaySlotGroups.map(([groupName, slots]) => (
                <div key={groupName} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-slate-900">{groupName}</h3>
                    <span className="text-sm text-slate-500">{slots.length} cupo(s) reales</span>
                  </div>
                  <div className="grid gap-3">
                    {slots.map((slot) => {
                      const selected = selectedSlots.some((item) => item.startAtUtc === slot.startAtUtc && item.serviceId === selectedServiceId)
                      return (
                        <button
                          key={slot.startAtUtc}
                          type="button"
                          onClick={() => toggleSlot(slot)}
                          className={`rounded-2xl border px-4 py-4 text-left transition ${selected ? 'border-emerald-400 bg-emerald-100' : 'border-emerald-200 bg-emerald-50 hover:border-emerald-300 hover:bg-emerald-100/70'}`}
                        >
                          <div className="text-base font-semibold text-slate-900">
                            {formatTimeLabel(new Date(slot.startAtUtc))} - {formatTimeLabel(new Date(slot.endAtUtc))}
                          </div>
                          <div className="mt-1 text-sm text-slate-600">
                            Duracion del servicio: {selectedService?.durationMinutes || 0} min
                          </div>
                          <div className="mt-3 text-sm font-medium text-emerald-700">
                            {selected ? 'Seleccionado para reservar' : 'Cupo disponible para reservar'}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )) : (
                <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4 text-sm text-slate-500">
                  No hay horarios disponibles para este dia.
                </div>
              )}
            </div>

            <div className="mt-6 rounded-2xl border border-stone-200 bg-stone-50 p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-sm text-slate-600">Espacios seleccionados</div>
                  <div className="text-2xl font-semibold text-slate-900">{selectedSlots.length}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-600">Total a pagar</div>
                  <div className="text-2xl font-semibold text-slate-900">{money(totalAmount)}</div>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap justify-end gap-3">
                <button
                  className="btn btn-primary"
                  type="button"
                  disabled={!validation?.isValid || selectedSlots.length === 0}
                  onClick={reserve}
                >
                  Reservar
                </button>
              </div>

              {!validation?.isValid && (
                <div className="mt-4 text-sm text-amber-700">
                  Primero valida tu codigo de invitacion para poder reservar.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isReservationsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 px-4 py-6">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-[28px] bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Reservas existentes</p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900">Tus reservas con este codigo</h2>
                <p className="mt-2 text-sm text-slate-600">
                  Aqui puedes revisar todo lo que ya tienes reservado antes de agendar nuevos espacios.
                </p>
              </div>
              <button className="btn btn-secondary" type="button" onClick={() => setIsReservationsModalOpen(false)}>
                Cerrar
              </button>
            </div>

            <div className="mt-6 grid gap-3 md:grid-cols-2">
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4">
                <div className="text-sm text-emerald-700">Total de reservas</div>
                <div className="mt-1 text-2xl font-semibold text-slate-900">{reservationTotals.count}</div>
              </div>
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4">
                <div className="text-sm text-emerald-700">Valor total reservado</div>
                <div className="mt-1 text-2xl font-semibold text-slate-900">{money(reservationTotals.amount)}</div>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              {allKnownReservations.map((item) => (
                <div key={item.appointmentId} className="rounded-2xl border border-stone-200 bg-stone-50 px-4 py-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="font-medium text-slate-900">{item.serviceName}</div>
                      <div className="mt-1 text-sm text-slate-600">
                        {new Date(item.startAtUtc).toLocaleDateString('es-CO')} · {formatTimeLabel(new Date(item.startAtUtc))} - {formatTimeLabel(new Date(item.endAtUtc))}
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-slate-900">{money(item.price)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {isConfirmationModalOpen && confirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 px-4 py-6">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-[28px] bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Reserva creada</p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900">Tu espacio fue reservado correctamente</h2>
                <p className="mt-2 text-sm text-slate-600">
                  Ya estas reservando con el codigo <strong>{confirmation.reservationCode}</strong>. Revisa el detalle del espacio y el pago por Nequi.
                </p>
              </div>
              <button className="btn btn-secondary" type="button" onClick={() => setIsConfirmationModalOpen(false)}>
                Cerrar
              </button>
            </div>

            <div className="mt-6 grid gap-3 md:grid-cols-2">
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4">
                <div className="text-sm text-emerald-700">Cliente</div>
                <div className="mt-1 text-lg font-semibold text-slate-900">{clientName || validation?.clientName || 'Cliente con invitacion validada'}</div>
              </div>
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4">
                <div className="text-sm text-emerald-700">Total reservado</div>
                <div className="mt-1 text-lg font-semibold text-slate-900">{money(confirmation.totalAmount)}</div>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              {latestConfirmationReservations.map((item) => (
                <div key={item.appointmentId} className="rounded-2xl border border-stone-200 bg-stone-50 px-4 py-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="font-medium text-slate-900">{item.serviceName}</div>
                      <div className="mt-1 text-sm text-slate-600">
                        {new Date(item.startAtUtc).toLocaleDateString('es-CO')} · {formatTimeLabel(new Date(item.startAtUtc))} - {formatTimeLabel(new Date(item.endAtUtc))}
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-slate-900">{money(item.price)}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5">
              <p className="text-sm font-semibold text-amber-800">Paga por Nequi para confirmar tu reserva</p>
              <p className="mt-2 text-3xl font-bold text-slate-900">{money(confirmation.totalAmount)}</p>
              <p className="mt-3 text-sm text-slate-700">
                Llave {confirmation.paymentKeyType || 'Nequi'}: <strong>{confirmation.paymentKey}</strong>
              </p>
              <p className="mt-1 text-xs text-slate-500">Tu reserva queda separada hasta {new Date(confirmation.expiresAtUtc).toLocaleString('es-CO')}.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
